<script lang="ts" setup>
import CoreButton from "@/components/common/CoreButton/CoreButton.vue";
import CoreLink from "@/components/common/CoreLink/CoreLink.vue";
import SetPasswordCard from "@/components/common/SetPasswordCard/SetPasswordCard.vue";
import { useCssVar } from "@vueuse/core";
import {
  ElForm,
  ElFormItem,
  ElInput,
  type FormRules,
  type FormInstance,
} from "element-plus";
import { useI18n } from "vue-i18n";
import { ref, reactive, computed } from "vue";
import useAuthStore from "@/stores/auth";

const buttonColor = useCssVar("--so-color-secondary");
const { t } = useI18n();
const emailSent = ref(false);
const formRef = ref<FormInstance>();
const authStore = useAuthStore();
const forgotPasswordSuccess = ref(false);
const title = computed(() =>
  emailSent.value ? "" : t("forms.login.recoveredPassword.title")
);
const subTitle = computed(() =>
  emailSent.value ? "" : t("forms.login.recoveredPassword.subtitle")
);

const disableForgotPassword = ref(false);

const submitForm = async () => {
  try {
    await formRef.value?.validate();
  } catch (err) {
    return;
  }

  disableForgotPassword.value = true;
  const { success, error } = await authStore.recoverPassword(form.email);
  emailSent.value = true;
  forgotPasswordSuccess.value = success || error;
};

const form = reactive({
  email: "",
});

const rules = reactive<FormRules>({
  email: [
    {
      required: true,
      message: () => t("forms.login.email.required"),
      trigger: "blur",
    },
    {
      type: "email",
      message: () => t("forms.login.email.invalid"),
      trigger: "blur",
    },
  ],
});
</script>
<template>
  <SetPasswordCard
    :title="title"
    :sub-title="subTitle"
  >
    <el-form
      ref="formRef"
      label-position="top"
      :rules="rules"
      class="form"
      :model="form"
      hide-required-asterisk
      @keydown.enter.prevent="submitForm"
    >
      <div v-if="forgotPasswordSuccess">
        <h2>{{ t("forms.login.recoveredPassword.successTitle") }}</h2>
        <span>{{ t("forms.login.recoveredPassword.successSubTitle") }}</span>
      </div>
      <el-form-item
        v-else
        :label="t('forms.login.recoveredPassword.label')"
        prop="email"
      >
        <div class="custom-form-item">
          <el-input
            v-model="form.email"
            type="email"
          />
          <CoreButton
            class="send-button"
            :color="buttonColor"
            :disabled="disableForgotPassword"
            @click="submitForm"
          >
            {{ t("forms.login.setPassword.sendButton") }}
          </CoreButton>
        </div>
      </el-form-item>

      <CoreLink
        :route="{ name: 'login' }"
        class="back-login"
        :underline="false"
        :style="{ color: buttonColor }"
      >
        {{ t("forms.login.submit") }}
      </CoreLink>
    </el-form>
  </SetPasswordCard>
</template>
<style lang="scss" scoped>
.form {
  margin-top: 30px;
}

.custom-form-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

:deep(.back-login) {
  margin-top: 11px;
}

.send-button {
  min-width: 155px;
  font-size: 1rem;
  font-weight: var(--font-semi-bold);
}

@media screen and (min-width: $breakpoint-sm) {
  .custom-form-item {
    flex-direction: row;
  }
}
</style>
